import { MessageCircle, Home, Smile, Paperclip } from 'lucide-react'
import PostEditor from '@/components/editor/PostEditor'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface ChatPreviewProps {
  body: string
  onBodyChange: (value: string) => void
  agent: Agent | undefined
  onImageUpload?: (file: File) => Promise<string>
}

function AgentAvatar({ agent }: { agent: Agent | undefined }) {
  if (agent?.avatar_url) {
    return <img src={agent.avatar_url} alt={agent.name} className="w-7 h-7 rounded-full object-cover" />
  }
  const initials = agent?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  return (
    <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium text-[10px]">
      {initials}
    </div>
  )
}

export default function ChatPreview({ body, onBodyChange, agent, onImageUpload }: ChatPreviewProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Browser Chrome */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 rounded-t-lg">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-400 border border-gray-200">
          seusite.com.br
        </div>
      </div>

      {/* Website area */}
      <div className="flex-1 bg-gray-50 relative rounded-b-lg" style={{ minHeight: '450px' }}>
        {/* Simulated page content */}
        <div className="p-6 space-y-3 opacity-30 pointer-events-none select-none absolute inset-0">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
          <div className="h-4 bg-gray-300 rounded w-1/2" />
          <div className="h-4 bg-gray-300 rounded w-5/6" />
          <div className="h-32 bg-gray-200 rounded mt-4" />
          <div className="h-4 bg-gray-300 rounded w-2/3" />
          <div className="h-4 bg-gray-300 rounded w-4/5" />
        </div>

        {/* Messenger + Launcher positioned bottom-right */}
        <div className="absolute bottom-4 right-4 flex flex-col items-end gap-3">
          {/* Messenger Window */}
          <div className="w-[320px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Messenger Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Home className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">Mensagens</span>
            </div>

            {/* Agent + Message Bubble */}
            <div className="p-3 space-y-2">
              {/* Agent info */}
              <div className="flex items-center gap-2">
                <AgentAvatar agent={agent} />
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs font-medium text-gray-900">{agent?.name || 'Agente'}</span>
                </div>
              </div>

              {/* Message bubble with PostEditor */}
              <div className="ml-9">
                <div className="bg-gray-50 rounded-lg text-sm text-gray-700 p-2">
                  <PostEditor
                    value={body}
                    onChange={onBodyChange}
                    placeholder="Escreva sua mensagem..."
                    minHeight="60px"
                    onImageUpload={onImageUpload}
                    hideButton
                  />
                </div>
              </div>
            </div>

            {/* Reply input (decorative) */}
            <div className="border-t border-gray-100 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center bg-gray-50 rounded-full px-3 py-1.5">
                  <span className="text-xs text-gray-400 flex-1">Escreva uma resposta...</span>
                  <div className="flex items-center gap-1.5 ml-2">
                    <Smile className="w-3.5 h-3.5 text-gray-400" />
                    <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Launcher */}
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    </div>
  )
}
